import { useEffect, useRef } from "react";
import {
  PickProductCard,
  type PickCardLabels,
  type PickProductItem,
} from "./pick/PickProductCard";
import { ctaTranslate } from "../lib/ctaTranslate";
import { useLabel } from "../lib/labels";
import { gaProductCardList } from "../lib/postmessage";

/* ─── data contracts (백엔드 응답 그대로 유지) ─── */

type ProductItem = PickProductItem;

interface AiTopPicksData {
  products?: ProductItem[];
  items?: ProductItem[];
  totalCount?: number;
  // display 표기언어(languageCode) 라벨 — 서버 i18n 해석본(없으면 카드 영어 폴백).
  labels?: PickCardLabels;
  metadata?: {
    root?: string;
    recommendationRound?: number;
    method?: string;
    base_sku?: string;
    base_price?: number;
    // EV2 — qa/stg 에서만 서버가 true 주입(prod 미발행). "Why?" 칩 게이트.
    showWhyEvidence?: boolean;
    [key: string]: unknown;
  };
}

interface Props {
  data: AiTopPicksData;
  sessionToken: string;
}

/* ─── 시안: guide U2 `.ai-gp1-pick--list` > `pick-list` (TU-02c) ─── */

export default function AiTopPicksCard({ data, sessionToken }: Props) {
  const products = data.products || data.items || [];
  const showWhyEvidence = Boolean(data.metadata?.showWhyEvidence);

  // 제품 카드 렌더 완료 1회 — asklg_shop_product_card_list (KPI: 관심 제품 생성률 Anchor).
  const gaListFiredRef = useRef(false);
  useEffect(() => {
    if (gaListFiredRef.current || products.length === 0) return;
    gaListFiredRef.current = true;
    gaProductCardList(products.map((p) => p.sku));
  }, [products]);

  const handleWhyEvidence = () => {
    void ctaTranslate({
      code: "evidence.why_ranked",
      sessionToken,
      fallback: "Why did these rank this way?",
      toolCall: { tool: "evidence", args: {} },
    });
  };

  const whyEvidenceLabel = useLabel("whyEvidence", "Why these picks?");

  if (products.length === 0) {
    return <div className="ac-hidden" aria-hidden="true" />;
  }

  return (
    <div className="ai-gp1--section">
      <div className="ai-gp1-pick--list">
        <ul className="pick-list">
          {products.map((item, idx) => (
            <PickProductCard
              key={item.sku || idx}
              item={item}
              sessionToken={sessionToken}
              labels={data.labels}
            />
          ))}
        </ul>
      </div>
      {showWhyEvidence ? (
        <div className="ac-evidence-why">
          <button
            type="button"
            className="ac-evidence-why__chip"
            onClick={handleWhyEvidence}
          >
            {whyEvidenceLabel}
          </button>
        </div>
      ) : null}
    </div>
  );
}
